import { store } from "@/app/store";
import { getUpstreamNodeIds } from "@/features/adp/components/canvas/canvas.utils";
import {
  extractAggregationReplayProjection,
  extractDropFieldsColumns,
  extractTransposeIgnoreColumns,
  type SourceDataGetter,
} from "@/features/adp/components/destination/dialogs/output-preview/outputPreview.utils";
import type { LoadTypeFormData } from "@/features/adp/schemas/LoadType";
import type {
  BulkUpdateSourceFileFieldItem,
  BulkUpdateSourceFileFieldsBody,
} from "@/features/adp/services/source/source.types";

export interface UpstreamSourceOriginalField {
  sourceNodeId: string;
  sourceFileId: number;
  sourceFileFieldId: number;
  fieldName: string;
}

function isUpstreamSourceOriginalFieldRow(
  row: unknown,
): row is { source_file_id: number; source_file_field_id: number; field_name: string } {
  if (!row || typeof row !== "object") return false;
  const candidate = row as Record<string, unknown>;
  return (
    typeof candidate.source_file_id === "number" &&
    typeof candidate.source_file_field_id === "number" &&
    typeof candidate.field_name === "string"
  );
}

/**
 * Original `field_name` values from upstream source file registration for a destination node.
 *
 * Uses the same `source_files` payload and column exclusion rules as output preview /
 * column mapping: Drop Fields (`DF`), Transpose `columnConfig` with `ignore` or
 * `transpose`, and—when an upstream Aggregation applies—the same replay projection
 * drop list. Does not add transformation-derived columns or renames / DC overrides.
 */
export function getUpstreamSourceOriginalFieldNames(
  destinationNodeId: string,
  getSourceDataByNodeId: SourceDataGetter,
): UpstreamSourceOriginalField[] {
  const { nodes, edges } = store.getState().canvas.present;
  const upstreamIds = getUpstreamNodeIds(destinationNodeId, edges);
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const upstreamTransformationIds = upstreamIds.filter(
    (id) => nodeMap.get(id)?.type === "transformation",
  );

  let sourceNodeId: string | null = null;
  let rawFiles: unknown[] = [];
  for (const id of upstreamIds) {
    const files = getSourceDataByNodeId(id)?.data?.source_files;
    if (Array.isArray(files) && files.length > 0) {
      sourceNodeId = id;
      rawFiles = files;
      break;
    }
  }
  const aggregationProjection = extractAggregationReplayProjection(
    upstreamTransformationIds,
    rawFiles,
  );
  const dropFields = [
    ...extractDropFieldsColumns(upstreamTransformationIds),
    ...extractTransposeIgnoreColumns(upstreamTransformationIds),
    ...(aggregationProjection?.dropFields ?? []),
  ];
  const dropSet = new Set(dropFields.map((c) => c.toLowerCase()));

  const seen = new Set<string>();
  const fields: UpstreamSourceOriginalField[] = [];
  if (!sourceNodeId) return fields;

  for (const row of rawFiles) {
    if (!isUpstreamSourceOriginalFieldRow(row)) continue;
    if (dropSet.has(row.field_name.toLowerCase())) continue;
    const trimmed = row.field_name.trim();
    const key = trimmed.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    fields.push({
      sourceNodeId,
      sourceFileId: row.source_file_id,
      sourceFileFieldId: row.source_file_field_id,
      fieldName: trimmed,
    });
  }
  return fields;
}

/**
 * Builds payload for `/source-file-fields/bulk-update` in incremental mode.
 * API invocation is handled by the caller; this utility only creates the body.
 */
export function buildIncrementalBulkUpdatePayload(
  values: LoadTypeFormData,
  sourceColumns: UpstreamSourceOriginalField[],
): BulkUpdateSourceFileFieldsBody {
  const updates: BulkUpdateSourceFileFieldItem[] = [];

  const selectedFlags: Array<{
    columnName: string | undefined;
    setKeys: BulkUpdateSourceFileFieldItem["set_keys"];
  }> = [
    {
      columnName: values.deleteIdentifier,
      setKeys: { is_deleted: true },
    },
    {
      columnName: values.createdDate,
      setKeys: { is_created_datetime: true },
    },
    {
      columnName: values.modifiedDate,
      setKeys: { is_last_modified_datetime: true },
    },
  ];

  for (const selection of selectedFlags) {
    if (!selection.columnName) continue;
    const match = sourceColumns.find((column) => column.fieldName === selection.columnName);
    if (!match) continue;

    updates.push({
      where_keys: {
        source_file_field_id: match.sourceFileFieldId,
        source_file_id: match.sourceFileId,
      },
      set_keys: selection.setKeys,
    });
  }

  return { updates };
}

export function applyIncrementalIdentifiersToSourceFiles(
  sourceFiles: unknown[],
  payload: BulkUpdateSourceFileFieldsBody,
): unknown[] {
  const updatesByFieldId = new Map<number, BulkUpdateSourceFileFieldItem["set_keys"]>();
  for (const item of payload.updates) {
    updatesByFieldId.set(item.where_keys.source_file_field_id, item.set_keys);
  }

  return sourceFiles.map((row) => {
    if (!isUpstreamSourceOriginalFieldRow(row)) return row;

    const setKeys = updatesByFieldId.get(row.source_file_field_id);
    if (!setKeys) {
      return {
        ...row,
        is_deleted: false,
        is_created_datetime: false,
        is_last_modified_datetime: false,
      };
    }

    return {
      ...row,
      is_deleted: Boolean(setKeys.is_deleted),
      is_created_datetime: Boolean(setKeys.is_created_datetime),
      is_last_modified_datetime: Boolean(setKeys.is_last_modified_datetime),
    };
  });
}
